import type { CRMUser } from '../types';

export type EorEngineerOption = { id: string; name: string; email?: string };

export type EorFirmOptions = {
  id: string;
  name: string;
  engineers: EorEngineerOption[];
};

/** "Tamarack Grove" on a user matches the firm "Tamarack Grove Engineering". */
export function companyMatchesEngineeringFirm(company: string, firmName: string): boolean {
  const norm = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(engineering|engineers|engineer|llc|inc|co|company|the)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const left = norm(company);
  const right = norm(firmName);
  if (!left || !right) return false;
  return left === right || right.startsWith(left) || left.startsWith(right);
}

/**
 * Firm directory plus active CRM engineers whose company matches the firm.
 * CRM engineers use their uid as the option id so Engineer of Record can grant hub access.
 */
export function engineeringFirmsWithCrmEngineers(
  firms: EorFirmOptions[] | undefined,
  crmUsers: CRMUser[],
): EorFirmOptions[] {
  return (firms || []).map((firm) => {
    const engineers = (firm.engineers || []).map((engineer) => ({ ...engineer }));
    const emails = new Set(
      engineers.map((engineer) => (engineer.email || '').trim().toLowerCase()).filter(Boolean),
    );
    const ids = new Set(engineers.map((engineer) => engineer.id));

    for (const user of crmUsers) {
      if (!user.isActive || user.role !== 'engineer') continue;
      if (!companyMatchesEngineeringFirm(user.company || '', firm.name)) continue;
      const email = (user.email || '').trim().toLowerCase();
      if ((email && emails.has(email)) || ids.has(user.uid)) continue;
      engineers.push({
        id: user.uid,
        name: user.displayName || user.email || 'Engineer',
        email: user.email,
      });
      if (email) emails.add(email);
      ids.add(user.uid);
    }

    engineers.sort((a, b) => a.name.localeCompare(b.name));
    return { ...firm, engineers };
  });
}
